/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import PageRecordCounter from '@/components/AZCustomComponent/PageRecordCounter';
import Hidden from '@/components/Hidden';
import styles from '../styles.module.scss';
import { parseUrl, stringifyUrl } from '@/utils/urlHelpers';
import { Actionable, ArrowLeft, Icon, View, Text } from '@az/starc-ui';
import { useRouter } from 'next/router';
import { useSearchPartTypes } from '../../api/getSearchPartTypes';
import { useLabels } from '@/hooks/useLabels';

const labelMap = {
  backLabel: 'label_back',
};

type Props = {
  setViewingPartTypes: (viewingPartTypes: boolean) => void;
  setAnalyticsCalled: (analyticsCalled: boolean) => void;
};

const PartTypesViewTopSection = ({ setViewingPartTypes, setAnalyticsCalled }: Props) => {
  const router = useRouter();
  const { query, url } = parseUrl(router.asPath);
  const { data: searchPartTypesData } = useSearchPartTypes({
    enabled: true,
    viewingPartTypes: true,
  });
  const { backLabel } = useLabels(labelMap);

  const rppOptionsData = '12,24,36,48';

  return (
    <View direction="row" align="end" padding={[5, 0]}>
      <View.Item grow>
        <View gap={1}>
          <View.Item>
            <Actionable
              type="button"
              onClick={() => {
                delete query.partTypesView;
                const newPathName = stringifyUrl({
                  url,
                  query,
                });

                void router.push(newPathName);
                setViewingPartTypes(false);
                setAnalyticsCalled(false);
              }}
            >
              <View align="center" gap={1} direction="row">
                <Icon svg={ArrowLeft} color="accent" />
                <Text
                  // TODO: remove className when STARC resolves https://gitlab.autozone.com/it/open-interface/starc/-/issues/27
                  className={styles.fontFamCond}
                  weight="medium"
                  lineHeight="140"
                  size="125"
                  textCase="uppercase"
                >
                  {backLabel}
                </Text>
              </View>
            </Actionable>
          </View.Item>
          {!!searchPartTypesData?.records?.length && (
            <Text
              // TODO: remove className when STARC resolves https://gitlab.autozone.com/it/open-interface/starc/-/issues/27
              className={styles.fontFamCond}
              variant="display-1"
              textCase="uppercase"
            >
              {searchPartTypesData.searchedKeyword}
            </Text>
          )}
        </View>
      </View.Item>
      {!!searchPartTypesData?.records?.length && (
        <View.Item>
          <Hidden smDown>
            <PageRecordCounter
              recordsPerPage={searchPartTypesData.recordsPerPage!}
              totalRecords={searchPartTypesData.total!}
              enableRecordsPerPageOptions={(searchPartTypesData.total ?? 0) > 24}
              recordsPerPageOptions={rppOptionsData}
              disableTotalMarkup
            />
          </Hidden>
        </View.Item>
      )}
    </View>
  );
};

export default PartTypesViewTopSection;
